import type { ChangeEvent } from "react";

import { startCase } from "lodash";
import moment from "moment";
import type { ControllerRenderProps, FieldValues, Path } from "react-hook-form";
import { z } from "zod";

import type { UseGetAllTransfersReturn } from "~/v2/api/hooks/useGetAllTransfers";
import { postFundingSurvey } from "~/v2/api/rest/banking";
import { FeatureFlags, useFeatureEnabled } from "~/v2/helpers/featureFlags";
import type { TransferSchema } from "../transferForm/schema";
import { WITHDRAW_REASONS_DETAILS_REQUIRED } from "./const";
import type { WithdrawReason } from "./types";

export const getLastTransferType = (
  transfers: UseGetAllTransfersReturn,
  direction: "INCOMING" | "OUTGOING",
): null | "ACH" | "Wire" | "CurrencyCloud" => {
  const isLoading = Object.values(transfers).some(
    (transferType) => transferType.isLoading,
  );
  const hasNoTransfers = Object.values(transfers).every(
    (transferType) => transferType.transfers.length === 0,
  );

  if (isLoading || hasNoTransfers) {
    return null;
  }

  const lastTransfer = Object.values(transfers)
    .flatMap(({ transfers }) => transfers)
    .filter((transfer) =>
      direction === "INCOMING"
        ? transfer.direction === "INCOMING"
        : transfer.direction !== "INCOMING",
    )
    .sort((a, b) => moment(b.created_at).diff(moment(a.created_at)))
    .at(0);

  if (!lastTransfer?.type) {
    return null;
  }

  const type = lastTransfer.type.toUpperCase();

  if (type === "ACH" || type === "INSTANT_ACH" || type === "RAPYD") {
    return "ACH";
  }

  if (type === "WIRE") {
    return "Wire";
  }

  if (type === "CURRENCY_CLOUD") {
    return "CurrencyCloud";
  }

  return null;
};

export const CURRENCY_FORMAT = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const formatCash = (value: number) => {
  if (Number.isNaN(value) || !Number.isFinite(value)) {
    return "Invalid amount";
  }
  return CURRENCY_FORMAT.format(value);
};

export const getCashTransformerV2 = <
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>({
  field,
}: {
  field: ControllerRenderProps<TFieldValues, TName>;
}) => {
  const transform = {
    // this will probably always be a pass-through
    input: (value: string) => value,
    // this will get called every time the input changes
    output: (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target?.value?.replace(/[^0-9.]/g, "") || "";
      const separator = value.includes(".") ? "." : "";
      const split = value.split(".");
      const base = split[0] !== "" ? parseInt(split[0], 10) : 0;
      const cents = split[1] || "";

      const formattedBase = base.toLocaleString("en-US");

      return `${
        base === 0 && value.startsWith("0") ? "0" : `${formattedBase}`
      }${separator}${cents.slice(0, 2)}`;
    },
  };

  return {
    ...field,
    placeholder: "0.00",
    value: transform.input(field.value),
    onChange: (event: ChangeEvent<HTMLInputElement>) =>
      field.onChange(transform.output(event)),
  };
};

export const getPercentTransformer = <
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>({
  field,
}: {
  field: ControllerRenderProps<TFieldValues, TName>;
}) => {
  const sanitize = (value: string) => value.replace(/[^0-9.]/g, "");

  return {
    ...field,
    value: field.value ?? "",
    onChange: (event: ChangeEvent<HTMLInputElement>) =>
      field.onChange(sanitize(event.target?.value ?? "")),
  };
};

export const isACHPendingMicroDeposit = (
  achRelationship?: undefined | { plaid_verification_status?: string | null },
) =>
  !!achRelationship &&
  !!achRelationship?.plaid_verification_status &&
  achRelationship.plaid_verification_status.includes("manual") &&
  achRelationship.plaid_verification_status.includes("pending");

export const getIraContributionTransferFields = (
  account?: null | { account_type?: string | null },
  date = new Date(),
) =>
  account?.account_type === "ira"
    ? {
        ira: {
          tax_year: String(date.getFullYear()),
        },
      }
    : {};

const getWithdrawalPercentage = (amount: number, withdrawable: number) => {
  if (amount <= 0 || withdrawable <= 0) {
    return 0;
  }
  return (amount / withdrawable) * 100;
};

export const getIsReasonRequired = (amount: number, withdrawable: number) => {
  const withdrawalPercentage = getWithdrawalPercentage(amount, withdrawable);
  return withdrawalPercentage >= 80;
};

export const getIsReasonDetailsRequired = (reason: TransferSchema["reason"]) =>
  Boolean(WITHDRAW_REASONS_DETAILS_REQUIRED.find((r) => r === reason));

export const postWithdrawSurvey = (
  accountID: string,
  survey: {
    reason: WithdrawReason;
    reasonDetails?: string;
  },
) => {
  const { reason, reasonDetails } = survey;

  // Let fail silently, no toast errors wanted
  try {
    postFundingSurvey(accountID, {
      withdraw_reason: reason,
      ...(reasonDetails && { withdraw_reason_details: reasonDetails }),
    });
  } catch (error) {
    window.env.ENV !== "PROD" &&
      console.error("Failed to post funding survey", error);
  }
};

export const useIsACHOptionAllowed = ({
  isLegalEntity,
  hasACHRelationship,
}: {
  isLegalEntity: boolean;
  hasACHRelationship: boolean;
}) => {
  const isACHLinkableLegalEntity = useFeatureEnabled(
    FeatureFlags.ACH_LINKABLE_LEGAL_ENTITIES,
  );

  if (!isLegalEntity) {
    return true;
  }
  return hasACHRelationship || isACHLinkableLegalEntity;
};

export const zNonEmptyString = z
  .string()
  .refine((s) => !!s?.trim(), { message: "Required" });

const DISPLAY_NAME_EXCLUDED_WORDS = ["LLC"];

export const getContraBrokerDisplayName = (name: string | undefined) =>
  (name ?? "")
    .split(" ")
    .map((word) =>
      DISPLAY_NAME_EXCLUDED_WORDS.includes(word) || word.includes(".")
        ? word
        : startCase(word.toLowerCase()),
    )
    .join(" ");
